import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getBadgeAwardDisputes, updateBadgeAwardDispute } from '../services/api';
import { getDateLocale } from '../i18n';

const BadgeDisputeManagementPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: '',
    manager_notes: '',
    resolution_notes: ''
  });

  useEffect(() => {
    fetchDisputes();
  }, [statusFilter]);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await getBadgeAwardDisputes(filters);
      setDisputes(response.results || response);
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
      setError(t('disputes.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (dispute) => {
    setSelectedDispute(dispute);
    
    // Initialize status to first valid transition, not current status
    let initialStatus = '';
    if (dispute.status === 'pending') {
      initialStatus = 'under_review';
    } else if (dispute.status === 'under_review') {
      initialStatus = 'resolved_upheld'; // Default to first option
    }
    
    setUpdateData({
      status: initialStatus,
      manager_notes: dispute.manager_notes || '',
      resolution_notes: dispute.resolution_notes || ''
    });
    setShowDetailModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedDispute) return;

    try {
      setUpdating(true);
      await updateBadgeAwardDispute(selectedDispute.id, updateData);
      await fetchDisputes();
      setShowDetailModal(false);
      setSelectedDispute(null);
    } catch (err) {
      console.error('Failed to update dispute:', err);
      alert(err.data?.error || t('disputes.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-700/70',
      'under_review': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70',
      'resolved_upheld': 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-700/70',
      'resolved_dismissed': 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80',
    };
    return colors[status] || colors['pending'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common:na');
    return new Date(dateString).toLocaleDateString(getDateLocale(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg dark:border dark:border-red-700/50">
              <span className="material-symbols-outlined text-red-600 text-3xl">gavel</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('disputes.title')}</h1>
              <p className="text-slate-600 mt-1">{t('disputes.subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-slate-700">{t('disputes.filterByStatus')}</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">{t('common:all')}</option>
            <option value="pending">{t('disputes.pending')}</option>
            <option value="under_review">{t('disputes.underReview')}</option>
            <option value="resolved_upheld">{t('disputes.resolvedUpheld')}</option>
            <option value="resolved_dismissed">{t('disputes.resolvedDismissed')}</option>
          </select>
        </div>
      </div>

      {/* Disputes List */}
      {error ? (
        <div className="bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-200">{error}</p>
        </div>
      ) : disputes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 border border-slate-200 text-center">
          <span className="material-symbols-outlined text-slate-400 text-5xl mb-4">inbox</span>
          <p className="text-slate-600">{t('disputes.noDisputes')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map((dispute) => (
            <motion.div
              key={dispute.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {dispute.badge_award?.badge?.name || t('disputes.unknownBadge')}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(dispute.status)}`}>
                      {dispute.status_display || dispute.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mb-3">
                    <div>
                      <span className="font-medium">{t('disputes.employee')}</span> {dispute.employee_name || dispute.employee}
                    </div>
                    <div>
                      <span className="font-medium">{t('disputes.submitted')}</span> {formatDate(dispute.submitted_at)}
                    </div>
                    {dispute.resolved_at && (
                      <div>
                        <span className="font-medium">{t('disputes.resolved')}</span> {formatDate(dispute.resolved_at)}
                      </div>
                    )}
                    {dispute.resolved_by_name && (
                      <div>
                        <span className="font-medium">{t('disputes.resolvedBy')}</span> {dispute.resolved_by_name}
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 mb-3">
                    <p className="text-sm font-medium text-slate-700 mb-1">{t('disputes.reason')}</p>
                    <p className="text-sm text-slate-600">{dispute.reason}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleViewDetails(dispute)}
                  className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <span className="material-symbols-outlined text-lg">visibility</span>
                  <span>{t('disputes.viewDetails')}</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedDispute && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-900">{t('disputes.disputeDetails')}</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <span className="material-symbols-outlined text-slate-600">close</span>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Badge Award Info */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3">{t('disputes.badgeAwardInfo')}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-slate-700">{t('disputes.badge')}</span>
                      <span className="ml-2 text-slate-600">{selectedDispute.badge_award?.badge?.name || t('common:na')}</span>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">{t('disputes.awardedDate')}</span>
                      <span className="ml-2 text-slate-600">
                        {formatDate(selectedDispute.badge_award?.awarded_date)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">{t('disputes.points')}</span>
                      <span className="ml-2 text-slate-600">{selectedDispute.badge_award?.points_earned || 0}</span>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">{t('disputes.team')}</span>
                      <span className="ml-2 text-slate-600">{selectedDispute.badge_award?.team_name || t('common:na')}</span>
                    </div>
                  </div>
                </div>

                {/* Employee Info */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3">{t('disputes.employeeInfo')}</h3>
                  <p className="text-sm text-slate-600">{selectedDispute.employee_name || selectedDispute.employee}</p>
                </div>

                {/* Dispute Reason */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">{t('disputes.disputeReason')}</h3>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedDispute.reason}</p>
                  </div>
                </div>

                {/* Status Update Form */}
                {selectedDispute.status !== 'resolved_upheld' && selectedDispute.status !== 'resolved_dismissed' && (
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="font-semibold text-slate-900 mb-4">{t('disputes.updateStatus')}</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">{t('disputes.status')}</label>
                        <select
                          value={updateData.status}
                          onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                        >
                          {selectedDispute.status === 'pending' && (
                            <option value="under_review">{t('disputes.underReview')}</option>
                          )}
                          {selectedDispute.status === 'under_review' && (
                            <>
                              <option value="resolved_upheld">{t('disputes.resolvedUpheld')}</option>
                              <option value="resolved_dismissed">{t('disputes.resolvedDismissed')}</option>
                            </>
                          )}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">{t('disputes.managerNotes')}</label>
                        <textarea
                          value={updateData.manager_notes}
                          onChange={(e) => setUpdateData({ ...updateData, manager_notes: e.target.value })}
                          rows={3}
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                          placeholder={t('disputes.managerNotesPlaceholder')}
                        />
                      </div>

                      {(updateData.status === 'resolved_upheld' || updateData.status === 'resolved_dismissed') && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">{t('disputes.resolutionNotes')}</label>
                          <textarea
                            value={updateData.resolution_notes}
                            onChange={(e) => setUpdateData({ ...updateData, resolution_notes: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400"
                            placeholder={t('disputes.resolutionNotesPlaceholder')}
                            required
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-3 pt-4">
                        <button
                          onClick={() => setShowDetailModal(false)}
                          className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                        >
                          {t('common:cancel')}
                        </button>
                        <button
                          onClick={handleUpdateStatus}
                          disabled={updating || !updateData.status || updateData.status === selectedDispute.status || (updateData.status === selectedDispute.status && !updateData.manager_notes && !updateData.resolution_notes)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          <span>{t('disputes.updateStatusButton')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Audit Trail / Timeline */}
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg">history</span>
                    {t('disputes.auditTrail')}
                  </h3>
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                    
                    <div className="space-y-4 relative">
                      {/* Submission Event */}
                      <div className="flex items-start gap-4">
                        <div className="relative z-10 flex-shrink-0">
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="material-symbols-outlined text-white text-sm">send</span>
                          </div>
                        </div>
                        <div className="flex-1 bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-700/50">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-blue-900">{t('disputes.disputeSubmitted')}</span>
                            <span className="text-xs text-blue-700">{formatDate(selectedDispute.submitted_at)}</span>
                          </div>
                          <p className="text-xs text-blue-800">{t('disputes.submittedBy', { name: selectedDispute.employee_name || selectedDispute.employee })}</p>
                        </div>
                      </div>

                      {/* Under Review Event */}
                      {selectedDispute.status === 'under_review' || selectedDispute.status === 'resolved_upheld' || selectedDispute.status === 'resolved_dismissed' ? (
                        <div className="flex items-start gap-4">
                          <div className="relative z-10 flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center">
                              <span className="material-symbols-outlined text-white text-sm">visibility</span>
                            </div>
                          </div>
                          <div className="flex-1 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-yellow-900">{t('disputes.underReview')}</span>
                              <span className="text-xs text-yellow-700">
                                {selectedDispute.status === 'under_review' ? t('disputes.inProgress') : formatDate(selectedDispute.submitted_at)}
                              </span>
                            </div>
                            {selectedDispute.manager_notes && (
                              <p className="text-xs text-yellow-800 mt-1">{t('disputes.managerNotesPrefix', { notes: selectedDispute.manager_notes })}</p>
                            )}
                          </div>
                        </div>
                      ) : null}

                      {/* Resolution Event */}
                      {(selectedDispute.status === 'resolved_upheld' || selectedDispute.status === 'resolved_dismissed') && (
                        <div className="flex items-start gap-4">
                          <div className="relative z-10 flex-shrink-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              selectedDispute.status === 'resolved_upheld' ? 'bg-green-500' : 'bg-gray-500'
                            }`}>
                              <span className="material-symbols-outlined text-white text-sm">check_circle</span>
                            </div>
                          </div>
                          <div className={`flex-1 rounded-lg p-3 border ${
                            selectedDispute.status === 'resolved_upheld' 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-sm font-semibold ${
                                selectedDispute.status === 'resolved_upheld' ? 'text-green-900' : 'text-gray-900'
                              }`}>
                                {selectedDispute.status_display || selectedDispute.status}
                              </span>
                              <span className={`text-xs ${
                                selectedDispute.status === 'resolved_upheld' ? 'text-green-700' : 'text-gray-700'
                              }`}>
                                {formatDate(selectedDispute.resolved_at)}
                              </span>
                            </div>
                            {selectedDispute.resolved_by_name && (
                              <p className={`text-xs mt-1 ${
                                selectedDispute.status === 'resolved_upheld' ? 'text-green-800' : 'text-gray-800'
                              }`}>
                                {t('disputes.resolvedByPrefix', { name: selectedDispute.resolved_by_name })}
                              </p>
                            )}
                            {selectedDispute.resolution_notes && (
                              <p className={`text-xs mt-2 ${
                                selectedDispute.status === 'resolved_upheld' ? 'text-green-800' : 'text-gray-800'
                              }`}>
                                {t('disputes.resolutionNotesPrefix', { notes: selectedDispute.resolution_notes })}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Resolved Info (Legacy - keeping for backward compatibility) */}
                {(selectedDispute.status === 'resolved_upheld' || selectedDispute.status === 'resolved_dismissed') && (
                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="font-semibold text-slate-900 mb-3">{t('disputes.resolutionDetails')}</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-slate-700">{t('disputes.status')}:</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedDispute.status)}`}>
                          {selectedDispute.status_display}
                        </span>
                      </div>
                      {selectedDispute.resolved_at && (
                        <div>
                          <span className="font-medium text-slate-700">{t('disputes.resolvedAt')}</span>
                          <span className="ml-2 text-slate-600">{formatDate(selectedDispute.resolved_at)}</span>
                        </div>
                      )}
                      {selectedDispute.resolved_by_name && (
                        <div>
                          <span className="font-medium text-slate-700">{t('disputes.resolvedBy')}</span>
                          <span className="ml-2 text-slate-600">{selectedDispute.resolved_by_name}</span>
                        </div>
                      )}
                      {selectedDispute.manager_notes && (
                        <div>
                          <span className="font-medium text-slate-700">{t('disputes.managerNotes')}:</span>
                          <p className="mt-1 text-slate-600 bg-slate-50 p-2 rounded whitespace-pre-wrap">{selectedDispute.manager_notes}</p>
                        </div>
                      )}
                      {selectedDispute.resolution_notes && (
                        <div>
                          <span className="font-medium text-slate-700">{t('disputes.resolutionNotes')}:</span>
                          <p className="mt-1 text-slate-600 bg-slate-50 p-2 rounded whitespace-pre-wrap">{selectedDispute.resolution_notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BadgeDisputeManagementPage;

